/**
 * Threshold Explorer — teaching module (education, not a calculator).
 *
 * Teaches diagnostic-threshold reasoning with the ESC 0/1-hour hs-cTnT
 * algorithm. All numbers are rendered from data/thresholdExplorer.json;
 * nothing clinical is hardcoded in this file. Dots are a teaching
 * simulation calibrated to the APACE+BACC cohort, not real patient data.
 *
 * Pattern only is borrowed from Stanford's aimeded explorer (draggable
 * thresholds + live metrics); all code and text here are original.
 * No analytics, no inline event handlers. All controls are keyboard
 * accessible (range inputs, radio group, checkbox, buttons).
 */

import { debugLog, debugError } from "../utils/logger.js";
import {
  classifyPatient,
  generateCohort,
  classifyCohort,
  orderThresholdLines,
  bayesNpv,
  bayesPpv,
} from "./thresholdExplorerMath.js";

const DATA_URL = "./data/thresholdExplorer.json";

const pct = (value, digits = 1) => `${(value * 100).toFixed(digits)}%`;
const fmtPct = (value) => (value === null ? "—" : pct(value, 1));

export class ThresholdExplorerModule {
  constructor() {
    this.data = null;
    this.dots = [];
    this.mountEl = null;
    this.initialized = false;
  }

  async init() {
    if (this.initialized) return;
    try {
      const res = await fetch(DATA_URL);
      if (!res.ok) throw new Error(`Failed to load explorer data: ${res.status}`);
      this.data = await res.json();
      const size = this.data.controls?.cohortSize ?? 200;
      const prevalence = (this.data.controls?.prevalence?.default ?? 17) / 100;
      this.dots = generateCohort({ size, prevalence });
      this.initialized = true;
      debugLog("Threshold Explorer: ready");
    } catch (error) {
      debugError("Threshold Explorer: initialization failed", error);
      throw error;
    }
  }

  /** Mount the module into an element. Safe to call once. */
  async mount(el) {
    this.mountEl = el;
    try {
      await this.init();
    } catch {
      el.innerHTML =
        `<div class="te-container"><p class="te-empty">Threshold Explorer content is unavailable right now.</p></div>`;
      return;
    }
    el.innerHTML = this.render();
    this._attach();
    this.update();
  }

  render() {
    const d = this.data;
    const c = d.controls;
    const cutoffs = d.cutoffs;
    const scenarios = d.scenarios
      .map(
        (s) =>
          `<button class="btn ghost te-scenario" type="button" data-scenario="${this._esc(s.id)}">${this._esc(s.label)}</button>`,
      )
      .join("");
    const learn = d.learnTopics
      .map(
        (t) =>
          `<details class="te-learn-item"><summary>${this._esc(t.title)}</summary><p>${this._esc(t.body)}</p></details>`,
      )
      .join("");
    const sources = d.sources
      .map((s) => {
        const title = s.url
          ? `<a href="${this._esc(s.url)}" target="_blank" rel="noopener noreferrer">${this._esc(s.label)}</a>`
          : this._esc(s.label);
        const citation = s.citation ? ` ${this._esc(s.citation)}` : "";
        return `<li>${title}.${citation} ${this._esc(s.covers)}</li>`;
      })
      .join("");
    return `
      <div class="te-container">
        <header class="te-header">
          <p class="workspace-eyebrow">Cardiology Learning</p>
          <h2 class="te-title">${this._esc(d.title)}</h2>
          <p class="te-subtitle">${this._esc(d.subtitle)}</p>
          <p class="te-disclaimer" role="note"><strong>Educational use only.</strong> ${this._esc(d.disclaimer)}</p>
          <p class="te-review" role="note"><strong>${this._esc(d.reviewLabel)}</strong></p>
          <p class="te-simulation-note" role="note">${this._esc(d.simulationNote)}</p>
        </header>

        <section class="te-controls" aria-label="Explorer controls">
          <div class="te-control">
            <label for="te-ruleout-line">Rule-out line (0-hour hs-cTnT, ng/L)</label>
            <input id="te-ruleout-line" type="range"
              min="${c.ruleOutLine.min}" max="${c.ruleOutLine.max}" step="${c.ruleOutLine.step}" value="${c.ruleOutLine.default}">
            <output id="te-ruleout-value" for="te-ruleout-line">${c.ruleOutLine.default} ng/L</output>
          </div>
          <div class="te-control">
            <label for="te-rulein-line">Rule-in line (0-hour hs-cTnT, ng/L)</label>
            <input id="te-rulein-line" type="range"
              min="${c.ruleInLine.min}" max="${c.ruleInLine.max}" step="${c.ruleInLine.step}" value="${c.ruleInLine.default}">
            <output id="te-rulein-value" for="te-rulein-line">${c.ruleInLine.default} ng/L</output>
          </div>
          <div class="te-control">
            <label for="te-prevalence">Population prevalence of NSTEMI (%)</label>
            <input id="te-prevalence" type="range"
              min="${c.prevalence.min}" max="${c.prevalence.max}" step="${c.prevalence.step}" value="${c.prevalence.default}">
            <output id="te-prevalence-value" for="te-prevalence">${c.prevalence.default}%</output>
          </div>
          <fieldset class="te-control te-fieldset">
            <legend>1-hour change applied to the illustration cohort</legend>
            <label><input type="radio" name="te-delta-mode" value="mixed" checked> Per-patient (as generated)</label>
            <label><input type="radio" name="te-delta-mode" value="small"> Small (&lt; ${cutoffs.noDelta} ng/L)</label>
            <label><input type="radio" name="te-delta-mode" value="large"> Large (&ge; ${cutoffs.ruleInDelta} ng/L)</label>
            <p class="te-hint">The small/large settings apply one change band to the whole illustration cohort — a teaching simplification. In real cohorts only a minority show a large rise.</p>
          </fieldset>
          <div class="te-control">
            <label><input id="te-onset" type="checkbox" checked> Chest-pain onset more than 3 hours before the 0-hour sample</label>
            <p class="te-hint">Required for direct rule-out from a very low 0-hour value (below ${cutoffs.veryLow} ng/L). Uncheck it to see the dots that depend on this condition change zones.</p>
          </div>
          <div class="te-control te-scenarios" role="group" aria-label="Illustration presets">
            ${scenarios}
            <p class="te-hint">Presets move the lines for teaching only. They are not guideline cutoffs.</p>
          </div>
        </section>

        <section class="te-plot-section" aria-label="Teaching cohort plot">
          <div class="te-axis-labels" aria-hidden="true">
            <span>0</span><span>${c.axisMax} ng/L (0-hour hs-cTnT)</span>
          </div>
          <div id="te-plot" class="te-plot" role="img" aria-label="Teaching cohort dot plot by 0-hour troponin value, colored by zone"></div>
          <p class="te-legend" aria-hidden="true">
            <span class="te-dot te-dot-ruleout"></span> Rule-out
            <span class="te-dot te-dot-observe"></span> Observe
            <span class="te-dot te-dot-rulein"></span> Rule-in
            <span class="te-dot te-dot-nstemi"></span> NSTEMI (filled)
            <span class="te-dot"></span> No NSTEMI (open)
          </p>
        </section>

        <section class="te-metrics" aria-live="polite" aria-label="Live metrics">
          <h3>What the illustration shows</h3>
          <div id="te-metrics-body"></div>
          <p class="te-hint">Illustration values come from the dots above. The APACE row below is the published validation result at the verified cutoffs and 17% prevalence.</p>
        </section>

        <section class="te-learn" aria-label="Learn">
          <h3>Learn</h3>
          ${learn}
        </section>

        <section class="te-sources" aria-label="Sources">
          <h3>Sources</h3>
          <ul>${sources}</ul>
        </section>
      </div>`;
  }

  _readControls() {
    const el = this.mountEl;
    const deltaMode =
      el.querySelector('input[name="te-delta-mode"]:checked')?.value ?? "mixed";
    return {
      ruleOutLine: Number(el.querySelector("#te-ruleout-line").value),
      ruleInLine: Number(el.querySelector("#te-rulein-line").value),
      prevalence: Number(el.querySelector("#te-prevalence").value) / 100,
      onsetOver3h: el.querySelector("#te-onset").checked,
      deltaMode,
    };
  }

  update() {
    const d = this.data;
    const controls = this._readControls();
    // Enforce strict line ordering so the plotted bands, the per-dot zones,
    // and the live counts can never disagree (see orderThresholdLines).
    const ordered = orderThresholdLines(controls.ruleOutLine, controls.ruleInLine, 1);
    controls.ruleOutLine = ordered.ruleOutLine;
    controls.ruleInLine = ordered.ruleInLine;
    const axisMax = d.controls.axisMax;
    const result = classifyCohort(this.dots, {
      ruleOutLine: controls.ruleOutLine,
      ruleInLine: controls.ruleInLine,
      cutoffs: d.cutoffs,
      onsetOver3h: controls.onsetOver3h,
      deltaMode: controls.deltaMode,
    });
    const el = this.mountEl;

    // Write the effective (possibly clamped) lines back so the sliders and
    // outputs always show the values the classification actually used.
    el.querySelector("#te-ruleout-line").value = controls.ruleOutLine;
    el.querySelector("#te-rulein-line").value = controls.ruleInLine;
    el.querySelector("#te-ruleout-value").textContent = `${controls.ruleOutLine} ng/L`;
    el.querySelector("#te-rulein-value").textContent = `${controls.ruleInLine} ng/L`;
    el.querySelector("#te-prevalence-value").textContent =
      `${Math.round(controls.prevalence * 100)}%`;

    // Plot: dots positioned by 0h value, colored by current zone.
    const plot = el.querySelector("#te-plot");
    const zoneOf = new Map();
    for (const dot of this.dots) {
      const delta =
        controls.deltaMode === "small"
          ? d.cutoffs.noDelta - 1
          : controls.deltaMode === "large"
            ? d.cutoffs.ruleInDelta + 1
            : dot.delta;
      zoneOf.set(
        dot.id,
        classifyPatient(dot.value0h, delta, {
          ruleOutLine: controls.ruleOutLine,
          ruleInLine: controls.ruleInLine,
          cutoffs: d.cutoffs,
          onsetOver3h: controls.onsetOver3h,
        }),
      );
    }
    const dotsHtml = this.dots
      .map((dot) => {
        const zone = zoneOf.get(dot.id);
        const left = Math.min(100, (dot.value0h / axisMax) * 100);
        const top = 8 + ((dot.id * 37) % 78);
        return `<span class="te-plot-dot te-plot-${zone}${dot.isNstemi ? " te-plot-nstemi" : ""}" style="left:${left.toFixed(1)}%;top:${top}%" aria-hidden="true"></span>`;
      })
      .join("");
    const ruleOutLeft = (controls.ruleOutLine / axisMax) * 100;
    const ruleInLeft = (controls.ruleInLine / axisMax) * 100;
    const veryLowLeft = (d.cutoffs.veryLow / axisMax) * 100;
    plot.innerHTML = `
      <div class="te-zone te-zone-ruleout" style="width:${ruleOutLeft.toFixed(1)}%"></div>
      <div class="te-zone te-zone-observe" style="left:${ruleOutLeft.toFixed(1)}%;width:${Math.max(0, ruleInLeft - ruleOutLeft).toFixed(1)}%"></div>
      <div class="te-zone te-zone-rulein" style="left:${ruleInLeft.toFixed(1)}%;width:${Math.max(0, 100 - ruleInLeft).toFixed(1)}%"></div>
      <div class="te-line te-line-verylow" style="left:${veryLowLeft.toFixed(1)}%" aria-hidden="true"></div>
      <div class="te-line te-line-ruleout" style="left:${ruleOutLeft.toFixed(1)}%" aria-hidden="true"></div>
      <div class="te-line te-line-rulein" style="left:${ruleInLeft.toFixed(1)}%" aria-hidden="true"></div>
      ${dotsHtml}`;
    plot.setAttribute(
      "aria-label",
      `Teaching cohort: ${result.counts["rule-out"].total} rule-out, ${result.counts.observe.total} observe, ${result.counts["rule-in"].total} rule-in at rule-out line ${controls.ruleOutLine} and rule-in line ${controls.ruleInLine} ng/L.`,
    );

    // Metrics: illustration values + Bayes at current prevalence + quoted APACE row.
    const derived = d.derivedCharacteristics;
    const bayesNpvValue = bayesNpv(
      derived.ruleOut.sensitivity,
      derived.ruleOut.specificity,
      controls.prevalence,
    );
    const bayesPpvValue = bayesPpv(
      derived.ruleIn.sensitivity,
      derived.ruleIn.specificity,
      controls.prevalence,
    );
    const v = d.validation;
    const total = this.dots.length;
    const zoneRow = (key, label) => {
      const z = result.counts[key];
      return `<tr><td>${label}</td><td>${z.total} (${pct(z.total / total, 0)})</td><td>${z.nstemi}</td></tr>`;
    };
    el.querySelector("#te-metrics-body").innerHTML = `
      <table class="te-table">
        <thead><tr><th>Zone</th><th>Illustration patients</th><th>With NSTEMI</th></tr></thead>
        <tbody>
          ${zoneRow("rule-out", "Rule-out")}
          ${zoneRow("observe", "Observe")}
          ${zoneRow("rule-in", "Rule-in")}
        </tbody>
      </table>
      <dl class="te-metrics-list">
        <div><dt>Illustration NPV (rule-out)</dt><dd>${fmtPct(result.npv)}</dd></div>
        <div><dt>Illustration PPV (rule-in)</dt><dd>${fmtPct(result.ppv)}</dd></div>
        <div><dt>Bayes NPV at current prevalence (derived characteristics)</dt><dd>${fmtPct(bayesNpvValue)}</dd></div>
        <div><dt>Bayes PPV at current prevalence (derived characteristics)</dt><dd>${fmtPct(bayesPpvValue)}</dd></div>
      </dl>
      <p class="te-apace"><strong>Published APACE+BACC result</strong> (verified cutoffs, 17% prevalence, n = ${v.n.toLocaleString()}): rule-out ${pct(v.ruleOut.rate, 0)} with NPV ${pct(v.ruleOut.npv)}; observe ${pct(v.observe.rate, 0)}; rule-in ${pct(v.ruleIn.rate, 0)} with PPV ${pct(v.ruleIn.ppv)}.</p>
      <p class="te-observe-risk"><strong>Observe is not low risk:</strong> ${this._esc(d.observeZoneRisk.note)}</p>
      <p class="te-hint">Sensitivity and specificity used for the Bayes rows are ${this._esc(derived.status)}: ${this._esc(derived.note)}</p>
      <p class="te-hint">Algorithm: ${this._esc(d.cutoffs.ruleOutRule)} ${this._esc(d.cutoffs.ruleInRule)}</p>`;
  }

  _attach() {
    const el = this.mountEl;
    for (const id of ["#te-ruleout-line", "#te-rulein-line", "#te-prevalence"]) {
      el.querySelector(id).addEventListener("input", () => this.update());
    }
    el.querySelector("#te-onset").addEventListener("change", () => this.update());
    for (const radio of el.querySelectorAll('input[name="te-delta-mode"]')) {
      radio.addEventListener("change", () => this.update());
    }
    for (const button of el.querySelectorAll(".te-scenario")) {
      button.addEventListener("click", () => {
        const scenario = this.data.scenarios.find(
          (s) => s.id === button.dataset.scenario,
        );
        if (!scenario) return;
        el.querySelector("#te-ruleout-line").value = scenario.ruleOutLine;
        el.querySelector("#te-rulein-line").value = scenario.ruleInLine;
        this.update();
      });
    }
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}

export const thresholdExplorerModule = new ThresholdExplorerModule();
